import MenuIcon from '@mui/icons-material/Menu'
import {AppBar, Box, type Breakpoint, Drawer, drawerClasses, IconButton, Stack, Toolbar} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import {memo, type PropsWithChildren, useState} from 'react'
import {Loader} from '../../components/Loader'
import {MercureMe} from '../../components/Mercure/MercureMe'
import {MercurePublic} from '../../components/Mercure/MercurePublic'
import {MercureSpot} from '../../components/Mercure/MercureSpot'
import {MercureUser} from '../../components/Mercure/MercureUser'
import {TextSkeleton} from '../../components/Skeleton/TextSkeleton'
import {useEffectEvent} from '../../hooks/useEffectEvent'
import {AppBarItems} from './components/Navigation/AppBarItems'
import {Menu} from './components/Navigation/Menu'
import {VisitListener} from './components/Navigation/VisitListener'
import {UserLoginPopup} from './components/UserLogin/UserLoginPopup'
import {PageProgress} from '../../components/PageProgress'

export const drawerWidth: number = 240
export const breakSizeNavbar: Breakpoint = 'lg'
export const breakSizeMobile: Breakpoint = 'sm'

interface DashboardAppBarProps {
  onToggleDrawer: () => void
}

const DashboardAppBar = memo(({onToggleDrawer}: DashboardAppBarProps) => {
  return <AppBar position="sticky">
    <Toolbar>
      <Loader>
        <Stack direction="row" sx={{width: '100%', gap: {xs: 1, sm: 2}}}>
          <Box sx={{flexShrink: 1, display: {[breakSizeNavbar]: 'none'}}}>
            <IconButton color="inherit" edge="start" onClick={onToggleDrawer}>
              <MenuIcon/>
            </IconButton>
          </Box>

          <AppBarItems/>
        </Stack>
      </Loader>
    </Toolbar>
  </AppBar>
})

interface DashboardDrawerProps {
  open: boolean
  onHideDrawer: () => void
}

const DashboardDrawer = memo(({open, onHideDrawer}: DashboardDrawerProps) => {
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down(breakSizeNavbar))

  return <Box component="nav" sx={{width: {[breakSizeNavbar]: drawerWidth}, flexShrink: {[breakSizeNavbar]: 0}}}>
    <Drawer
      variant={mobile ? 'temporary' : 'permanent'}
      open={mobile ? open : true}
      onClick={onHideDrawer}
      onClose={mobile ? onHideDrawer : () => {
      }}
      ModalProps={{
        keepMounted: mobile, // Better open performance on mobile.
      }}
      sx={{
        position: 'absolute',
        zIndex: (theme) => theme.zIndex.appBar - 1,
        width: drawerWidth,
        [`& .${drawerClasses.paper}`]: {
          width: 'inherit',
        },
      }}
    >
      <Loader fallback={<TextSkeleton/>}>
        <Menu/>
      </Loader>
    </Drawer>
  </Box>
})

export const Dashboard = memo(({children}: PropsWithChildren) => {
  const [open, setOpen] = useState(false)

  const toggleDrawer = useEffectEvent(() => {
    setOpen((open) => !open)
  })
  const hideDrawer = useEffectEvent(() => {
    setOpen(false)
  })

  return <>
    <DashboardAppBar onToggleDrawer={toggleDrawer}/>

    <Box sx={{display: 'flex'}}>
      <DashboardDrawer onHideDrawer={hideDrawer} open={open}/>

      <Box sx={{
        flexGrow: 1,
        maxWidth: {xs: '100%', [breakSizeNavbar]: `calc(100% - ${drawerWidth}px)`},
      }}>
        <Box component="main" sx={{position: 'relative'}}>
          {children}
        </Box>
      </Box>
    </Box>
    <Loader fallback={<PageProgress/>}>
      <UserLoginPopup/>
    </Loader>
    <VisitListener/>
    <MercureMe/>
    <MercureSpot/>
    <MercureUser/>
    <MercurePublic/>
  </>
})
